import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const storageKey = (id: string) => `section-collapsed:${id}`;

/**
 * A page section whose body can be folded away. The choice is remembered per
 * section (not per record), so someone who never looks at a section keeps it
 * closed on every page that has it.
 */
export function CollapsibleSection({
  id,
  title,
  aside,
  action,
  children,
}: {
  /** Stable name of the section, used to remember whether it is folded. */
  id: string;
  title: ReactNode;
  /** Shown right after the title, e.g. status badges. */
  aside?: ReactNode;
  /** Shown at the far end of the header, e.g. an "add" button. */
  action?: ReactNode;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(storageKey(id)) === "1",
  );

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (next) localStorage.setItem(storageKey(id), "1");
    else localStorage.removeItem(storageKey(id));
  };

  const Chevron = collapsed ? ChevronRight : ChevronDown;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={!collapsed}
            title={collapsed ? "Rozwiń sekcję" : "Zwiń sekcję"}
            className="-ml-1 flex items-center gap-1 rounded-md px-1 hover:bg-muted"
          >
            <Chevron className="size-4 text-muted-foreground" />
            <h2 className="text-lg font-semibold">{title}</h2>
          </button>
          {aside}
        </div>
        {action}
      </div>
      {!collapsed && children}
    </section>
  );
}
